import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { camelCase_to_Sentence, pluralReadableTypeName, toTitleCase } from '@iotv/core-services';
import { Assembler, BooleanRuleClassName, BranchingQueryDefinition, ComparisonRuleClassName, Device, edgeTypeMatrix, getParentEdgeTypeId, getZombieInstanceFromPrimaryKey, GroupClassName, MetricRuleClassName, pluralize, Ruleset, TimeBucketUnits, } from '@iotv/datamodel';
import { matchEntityStates } from '@iotv/entity-state';
import { deviceEnabledTypes, getDeviceEnabledTypes } from '@iotv/payload-definition';
import { AddRounded } from '@mui/icons-material';
import SaveIcon from '@mui/icons-material/Save';
import { LoadingButton } from '@mui/lab';
import { AccordionActions, Alert, alertClasses, Autocomplete, Button, Card, CardContent, FormControlLabel, FormLabel, Grid, IconButton, Skeleton, Stack, Switch, TextField, Tooltip, Typography, } from '@mui/material';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ConsumerDao } from '../../../data/ConsumerDAO';
import { getPseudoVBO } from '../../../data/TypeHelpers';
import { isAdminRole } from '../../../helpers/access';
import { getListing } from '../../../helpers/format';
import { useHoursPeriodSelector } from '../../../hooks/useHoursPeriodSelector';
import { ActionIcons, FeatureIcons } from '../../../Iconography';
import { AdjacentType, Severity } from '../../../types';
import { AutosizedPopper } from '../../common/AutosizedPopper';
import { AutosizedTextField } from '../../common/AutosizedTextField';
import { ConfirmButton } from '../../common/ConfirmButton';
import { ConfirmLoadingButton } from '../../common/ConfirmLoadingButton';
import { IoTVAccordion, IoTVAccordionDetails, IoTVAccordionSummary } from '../../common/IoTVAccordion';
import { displayMessage } from '../../messages/displayMessage';
import { matching } from '../Utilities/CompareRulesets';
import { GlobalIcon } from '../Utilities/GlobalIcon';
import { RulesetBooleanRuleCard } from './BooleanRuleCard';
import { RulesetComparisonRuleCard } from './ComparisonRuleCard';
import { MessageTemplate } from './MessageTemplate';
import { RulesetMetricRuleCard } from './MetricRuleCard';
const debug = process.env.REACT_APP_DEBUG && false;
const effectDebug = process.env.REACT_APP_DEBUG && true;
export const RulesetCard = (props) => {
    const { contextCustomer, user, userGroupRoles, userSelectedGroup, userFunctions, isSmallView, allowedActions } = props;
    debug && console.log('RulesetCard props', props);
    const { seedCustomer, viewObject: { matchedPrimary }, } = props;
    const rulesetIn = matchedPrimary;
    const [ruleset, setRuleset] = useState(rulesetIn ? new Ruleset(rulesetIn) : undefined);
    const editable = !!allowedActions?.edit;
    const removable = !!allowedActions?.delete;
    const [saving, setSaving] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const noRuleset = ruleset === undefined;
    const hasRuleset = !noRuleset;
    const noPeriod = hasRuleset && ruleset.period === undefined;
    const noUnit = hasRuleset && ruleset.unit === undefined;
    const hasMetricRules = ruleset?.definition.MetricRule !== undefined && 0 < ruleset.definition.MetricRule.length;
    const hasComparisonRules = ruleset?.definition.ComparisonRule !== undefined && 0 < ruleset.definition.ComparisonRule.length;
    const hasAveragedMetricRules = hasMetricRules && ruleset.definition.MetricRule?.map((r) => r.valueType).includes('averaged');
    const hasSummedMetricRules = hasMetricRules && ruleset.definition.MetricRule?.map((r) => r.valueType).includes('bucketed');
    const hasAveragedComparisonRules = hasComparisonRules && ruleset.definition.ComparisonRule?.map((r) => r.valueType).includes('averaged');
    const hasSummedComparisonRules = hasComparisonRules && ruleset.definition.ComparisonRule?.map((r) => r.valueType).includes('bucketed');
    const hasTimeDependentMetricRules = hasAveragedMetricRules || hasSummedMetricRules;
    const hasTimeDependentComparisonRules = hasAveragedComparisonRules || hasSummedComparisonRules;
    const hasTimeDependentRules = hasTimeDependentMetricRules || hasTimeDependentComparisonRules;
    useEffect(() => {
        if (rulesetIn !== undefined)
            setRuleset(new Ruleset(rulesetIn));
    }, [rulesetIn]);
    const [querying, setQuerying] = useState(false);
    const [global, setGlobal] = useState(undefined);
    const mismatched = global !== undefined && ruleset !== undefined ? matching(global, ruleset) : [];
    useEffect(() => {
        if (seedCustomer && seedCustomer.sk !== contextCustomer?.sk && global === undefined && rulesetIn !== undefined && ruleset?.sk && !querying) {
            setQuerying(true);
            ConsumerDao.getGraphQueryRequestV2({
                contextObjects: [seedCustomer],
                query: new BranchingQueryDefinition({ id: seedCustomer.sk, path: [{ objectTypeId: 'Ruleset', adjacencyType: AdjacentType.CHILD }] }),
            }).then(({ err, data }) => {
                setQuerying(false);
                if (err || !data)
                    return;
                const g = data.getNodesOfType('Ruleset').find((r) => r.global && r.name === ruleset.name);
                if (g)
                    setGlobal(new Ruleset(g));
            });
        }
    }, [ruleset?.sk, saving]);
    const [setOperation, setSetOperation] = useState(ruleset?.setOperation);
    const [linkedGroups, setLinkedGroups] = useState([]);
    const [metricKeysEnum, setMetricKeysEnum] = useState({});
    const [ruleAttributes, setRuleAttributes] = useState({});
    const [userAttributes, setUserAttributes] = useState({});
    const [groups, setGroups] = useState([]);
    // save validation
    const [invalid, setInvalid] = useState(false);
    useEffect(() => {
        const noName = ruleset?.name === undefined || ruleset.name === '';
        const noAppliesToType = ruleset?.appliesToType === undefined || ruleset.appliesToType === '';
        const noTime = ruleset?.period === undefined || ruleset.period === 0 || ruleset.unit === undefined;
        const hasMetricRules = ruleset?.definition.MetricRule !== undefined && 0 < ruleset.definition.MetricRule.length;
        const hasAveragedRulesWithoutTime = noTime && hasMetricRules && ruleset.definition.MetricRule?.map((r) => r.valueType).includes('averaged');
        const hasBucketedRulesWithoutTime = noTime && hasMetricRules && ruleset.definition.MetricRule?.map((r) => r.valueType).includes('bucketed');
        const noGroups = ruleset?.globallyApplyToObjectTypeId === false && linkedGroups.length === 0;
        const noOperation = ruleset?.setOperation === undefined;
        setInvalid(noName || noAppliesToType || hasAveragedRulesWithoutTime || hasBucketedRulesWithoutTime || noGroups || noOperation);
    }, [ruleset, linkedGroups]);
    const fetch = async () => {
        if (ruleset?.appliesToType && contextCustomer) {
            // fetch all groups for the entity type
            let items = [];
            const allGroups = await ConsumerDao.getGraphQueryRequestV2({
                contextObjects: [contextCustomer],
                query: new BranchingQueryDefinition({ id: contextCustomer.sk, path: [{ objectTypeId: GroupClassName, adjacencyType: AdjacentType.CHILD }] }),
            });
            if (allGroups.err)
                return;
            items =
                allGroups.data
                    ?.getNodesOfType(GroupClassName)
                    ?.filter((group) => group && group.supportedType === ruleset.appliesToType)
                    .map((group) => Assembler.getInstance(group))
                    .filter((group) => group !== undefined) ?? [];
            setGroups(items);
            // fetch linked groups
            const { err, data } = await ConsumerDao.getGraphQueryRequestV2({
                contextObjects: [ruleset],
                query: new BranchingQueryDefinition({ id: ruleset.sk, path: [{ objectTypeId: GroupClassName, adjacencyType: AdjacentType.CHILD }] }),
            });
            if (err)
                return;
            if (data)
                setLinkedGroups(data.getNodesOfType(GroupClassName));
        }
    };
    useEffect(() => {
        if (ruleset?.appliesToType)
            fetch();
    }, [ruleset?.appliesToType]);
    /**
     * Does database graph queries to ensure that links are setup properly for Entity -> Group(s) -> Ruleset(s)
     * In theory, the UI only allows for adding / removing a single group at a time, so this should only generate one network request
     * @param oldGroups list of current Group sk's in use
     * @param newGroups list of updated Group sk's to use
     */
    const updateGroupLinks = (oldGroups, newGroups) => {
        const removedGroups = oldGroups.filter((sk) => !newGroups.includes(sk));
        const addedGroups = newGroups.filter((sk) => !oldGroups.includes(sk));
        if (ruleset)
            removedGroups.forEach(async (sk) => {
                const [type, id] = sk.split(':');
                const group = { pk: sk, sk, type, id };
                const { err, data } = await ConsumerDao.unlink(ruleset, group);
                if (!err && data)
                    userFunctions.removeObjectsFromState([data.removedItem]);
            });
        if (ruleset)
            addedGroups.forEach(async (sk) => {
                const [type, id] = sk.split(':');
                const group = { pk: sk, sk, type, id };
                const { err, data } = await ConsumerDao.link(ruleset, group, getParentEdgeTypeId(type, ruleset.type));
                if (!err && data) {
                    const [source, edge, target] = data;
                    if (edge)
                        userFunctions.addObjectsToState([edge]);
                }
            });
        return newGroups;
    };
    const { components: HoursPeriodSelector, fromTimeValue, toTimeValue, } = useHoursPeriodSelector({
        validBetween: ruleset?.validBetween,
        disabled: !editable || saving || updating || deleting,
    });
    useEffect(() => {
        if (ruleset)
            ruleset.validBetween = {
                from: { hour: (fromTimeValue?.hour() ?? 0), minute: (fromTimeValue?.minute() ?? 0) },
                to: { hour: (toTimeValue?.hour() ?? 23), minute: (toTimeValue?.minute() ?? 59) },
            };
    }, [fromTimeValue, toTimeValue]);
    const handleRequestVideoFootageToggle = () => {
        setRuleset((prevRuleset) => {
            const updated = { ...prevRuleset, requestVideoFootage: !prevRuleset?.requestVideoFootage };
            return updated;
        });
    };
    const saveRuleset = async () => {
        setSaving(true);
        if (contextCustomer?.sk && ruleset) {
            const results = [];
            const saveRes = await ConsumerDao.save(ruleset);
            if (saveRes.data) {
                results.push({ err: null, data: [contextCustomer, saveRes.data, ruleset] });
            }
            else {
                results.push({ err: { message: `Ruleset save returned no object`, zombieA: contextCustomer, zombieB: ruleset }, data: null });
            }
            results.push(await ConsumerDao.link(contextCustomer, ruleset, edgeTypeMatrix.Customer.Ruleset.customerHasRuleset.edgeTypeId));
            const linking = linkedGroups.map(async (g) => {
                results.push(await ConsumerDao.link(ruleset, g, getParentEdgeTypeId(g.type, ruleset.type)));
            });
            await Promise.all(linking);
            const err = results.filter((r) => r.err !== null).shift();
            if (err) {
                displayMessage(`Ruleset ${ruleset.name} not saved: ${err}`, 'SnackBar', userFunctions, Severity.error);
            }
            else {
                displayMessage(`Ruleset ${ruleset.name} saved`, 'SnackBar', userFunctions, Severity.success);
                props.updateInParent?.(ruleset);
            }
        }
        setSaving(false);
    };
    const updateRulesetFromGlobal = async () => {
        if (!contextCustomer || !global || !ruleset)
            return;
        setUpdating(true);
        debug && console.log('updateRulesetFromGlobal', 'updating...');
        const { pk, sk, id, ...rest } = global;
        const clone = new Ruleset({ ...ruleset, ...rest });
        debug && console.log('updateRulesetFromGlobal', global.id, ruleset.id, clone.id, clone);
        setRuleset(clone);
        debug && console.log('updateRulesetFromGlobal', 'updated local ruleset');
        setUpdating(false);
    };
    const deleteRuleset = async () => {
        if (contextCustomer && ruleset) {
            setDeleting(true);
            const results = [];
            const unlinking = linkedGroups.map(async (g) => {
                results.push(await ConsumerDao.unlink(ruleset, g));
            });
            await Promise.all(unlinking);
            results.push(await ConsumerDao.unlink(contextCustomer, ruleset));
            results.push(await ConsumerDao.deleteExact(ruleset));
            const err = results.filter((r) => r.err !== null).shift();
            if (err) {
                displayMessage(`Ruleset ${ruleset.name} not deleted: ${err}`, 'SnackBar', userFunctions, Severity.error);
                setDeleting(false);
            }
            else {
                displayMessage(`Ruleset ${ruleset.name} deleted`, 'SnackBar', userFunctions, Severity.success);
                props.notifyDeleted?.();
            }
        }
    };
    function getDeviceType(appliesToType) {
        const deviceEnabledTypes = getDeviceEnabledTypes();
        effectDebug && console.log('DeviceEnabledTypes', deviceEnabledTypes);
        const deviceType = deviceEnabledTypes[appliesToType]?.deviceTypes[0]?.deviceType;
        effectDebug && console.log('DeviceType', deviceType);
        return deviceType;
    }
    /**
     * This fetches the appropriate attributes that metric rules can use
     * It assumes that entity-state/SupportedEntities/Entity/StateType.calculated is correct, and
     * that the venture's calculateState function uses it accordingly, whilst propagating changes in to the ObjectHistory
     * This is the responsibility of the venture developer!
     */
    useEffect(() => {
        if (ruleset?.appliesToType) {
            // fetch the CalculatedState class for the entity that matches with the device
            try {
                // fetch a zombie device that the ruleset will process
                const deviceType = getDeviceType(ruleset.appliesToType);
                const device = new Device({
                    ...getZombieInstanceFromPrimaryKey(`Device:ABC`),
                    deviceType,
                });
                const entity = Assembler.getInstanceFromPrimaryKey(`${ruleset.appliesToType}:xxx`);
                const entityStateBundle = matchEntityStates({ device, entity })[0];
                effectDebug && console.log('RulesetCard', 'appliesToType', ruleset.appliesToType);
                effectDebug && console.log('RulesetCard', 'deviceType', deviceType);
                effectDebug && console.log('RulesetCard', 'device', device);
                effectDebug && console.log('RulesetCard', 'entity', entity);
                effectDebug && console.log('RulesetCard', 'entityStateBundle', entityStateBundle);
                if ('getUserAttributes' in entity)
                    setUserAttributes(entity.getUserAttributes());
                const calcStateClass = entityStateBundle.CalculatedState;
                const calcStateInstance = calcStateClass ? new calcStateClass() : {};
                const metricKeysEnum = calcStateClass
                    ? Object.fromEntries(Object.keys(calcStateInstance)
                        .sort((a, b) => a.localeCompare(b))
                        .map((k) => [k, k]))
                    : {};
                setMetricKeysEnum(metricKeysEnum);
                const attributes = calcStateClass.getRuleAttributes?.();
                if (attributes !== undefined)
                    setRuleAttributes(attributes);
            }
            catch (e) {
                console.log(`RulesetCard: Err resolving payload`, e.message, e.stack);
            }
        }
        else {
            console.warn("RulesetCard - no ruleset applies to type, can't set keys!");
        }
    }, [ruleset?.appliesToType]);
    useEffect(() => {
        setRuleset((r) => {
            const newRuleset = new Ruleset(r ?? {});
            newRuleset.setOperation = setOperation;
            return newRuleset;
        });
    }, [setOperation]);
    const addBooleanRule = () => {
        const newRuleset = new Ruleset(ruleset ?? {});
        if (!newRuleset.definition.BooleanRule)
            newRuleset.definition.BooleanRule = [];
        newRuleset.definition.BooleanRule.push(getPseudoVBO({ id: uuidv4(), type: BooleanRuleClassName, name: 'new Boolean Rule', enabled: true }));
        setRuleset(newRuleset);
    };
    const addMetricRule = () => {
        const newRuleset = new Ruleset(ruleset ?? {});
        if (!newRuleset.definition.MetricRule)
            newRuleset.definition.MetricRule = [];
        newRuleset.definition.MetricRule.push(getPseudoVBO({ id: uuidv4(), type: MetricRuleClassName, name: 'new Metric Rule', enabled: true }));
        setRuleset(newRuleset);
    };
    const addComparisonRule = () => {
        const newRuleset = new Ruleset(ruleset ?? {});
        if (!newRuleset.definition.ComparisonRule)
            newRuleset.definition.ComparisonRule = [];
        newRuleset.definition.ComparisonRule.push(getPseudoVBO({ id: uuidv4(), type: ComparisonRuleClassName, name: 'new Comparison Rule', enabled: true }));
        setRuleset(newRuleset);
    };
    const components = {
        name: (_jsxs(Stack, { direction: isSmallView ? 'column' : 'row', alignItems: "center", gap: 1, children: [seedCustomer ? (!querying ? (_jsx(Tooltip, { title: ruleset?.global ? 'Ruleset is global' : 'Ruleset is local', placement: "right", arrow: true, children: _jsx("span", { children: _jsx(IconButton, { disabled: !editable || saving || updating || deleting || !isAdminRole(userSelectedGroup), onClick: () => {
                                setRuleset((r) => {
                                    const newRuleset = new Ruleset(r ?? {});
                                    newRuleset.global = !newRuleset.global;
                                    return newRuleset;
                                });
                            }, children: _jsx(GlobalIcon, { global: seedCustomer.sk === contextCustomer?.sk ? ruleset : global, local: ruleset, mismatched: mismatched }) }) }) })) : (_jsx(Skeleton, { variant: "circular", width: "1.5em", height: "1.5em", sx: { marginInline: 1 } }))) : (_jsx(_Fragment, {})), editable && (_jsx(LoadingButton, { startIcon: _jsx(SaveIcon, {}), variant: "outlined", color: "primary", fullWidth: isSmallView, onClick: saveRuleset, disabled: invalid || saving || updating || deleting, loading: saving, loadingPosition: "start", children: "Save" })), removable && (_jsx(ConfirmLoadingButton, { startIcon: _jsx(ActionIcons.Delete, {}), color: "error", variant: "outlined", fullWidth: isSmallView, onConfirm: deleteRuleset, confirmMessage: "Confirm?", disabled: saving || updating || deleting, loading: deleting, children: "Remove" })), _jsx(ConfirmLoadingButton, { startIcon: _jsx(FeatureIcons.Video, {}), color: "primary", variant: "outlined", fullWidth: isSmallView, onConfirm: handleRequestVideoFootageToggle, confirmMessage: "Confirm?", disabled: saving || updating || deleting, loading: saving || updating || deleting, children: ruleset?.requestVideoFootage ? 'Remove Video Request' : 'Set Video Request' }), invalid && (_jsx(Alert, { severity: "error", sx: { paddingBlock: 0, alignItems: 'center' }, children: "Please fill all required fields before saving!" })), seedCustomer !== undefined && seedCustomer.sk !== contextCustomer?.sk && ruleset?.global && !querying && global === undefined && (_jsx(Alert, { severity: "warning", sx: { paddingBlock: 0, alignItems: 'center' }, children: "No global ruleset found!" })), 0 < mismatched.length && (_jsxs(Alert, { severity: "warning", action: editable && (_jsx(ConfirmButton, { color: "inherit", confirmMessage: "Update?", onConfirm: updateRulesetFromGlobal, loading: updating, disabled: saving || updating || deleting, children: "Update" })), sx: { paddingBlock: 0, paddingInlineEnd: 1, alignItems: 'center', [`& .${alertClasses.action}`]: { paddingBlock: 0 } }, children: ["Mismatched \u2014 ", getListing(mismatched, 'all'), " not matching!"] }))] }, 'title')),
        description: (_jsxs(Grid, { container: true, rowSpacing: 1, size: { xs: 12, md: 6 }, children: [_jsx(Grid, { size: 12, children: _jsx(AutosizedTextField, { value: ruleset?.name ?? '', label: "Name", size: "small", inputProps: { style: { fontSize: 'xx-large' } }, fullWidth: true, error: !deleting && (!ruleset?.name || ruleset.name === ''), disabled: !editable || saving || deleting, onChange: (event) => {
                            setRuleset((r) => {
                                const newRuleset = new Ruleset(r ?? {});
                                newRuleset.name = event.target.value;
                                return newRuleset;
                            });
                        } }) }), _jsx(Grid, { size: 12, children: _jsx(TextField, { value: ruleset?.description ?? '', label: "Description", multiline: true, minRows: 2, maxRows: 10, size: "small", fullWidth: true, disabled: !editable || saving || updating || deleting, onChange: (event) => {
                            setRuleset((r) => {
                                const newRuleset = new Ruleset(r ?? {});
                                newRuleset.description = event?.target.value;
                                return newRuleset;
                            });
                        } }) })] }, "name_description")),
        trigger: (_jsxs(Grid, { container: true, rowSpacing: 1, columnSpacing: 1, style: { alignContent: 'flex-start' }, size: { xs: 12, md: 6 }, children: [_jsxs(Grid, { container: true, size: 12, children: [_jsx(Grid, { size: { xs: 12, md: 'grow' }, children: _jsx(Autocomplete, { value: ruleset?.appliesToType ?? '', options: Object.keys(deviceEnabledTypes), getOptionLabel: (o) => toTitleCase(camelCase_to_Sentence(o)), freeSolo: false, disabled: !editable || saving || updating || deleting || linkedGroups.length !== 0, onChange: (_, value) => {
                                    if (!value)
                                        return;
                                    setRuleset((r) => {
                                        const newRuleset = new Ruleset(r ?? {});
                                        newRuleset.appliesToType = value;
                                        return newRuleset;
                                    });
                                }, renderInput: (props) => {
                                    return _jsx(TextField, { ...props, label: "Entity", size: "small", error: !deleting && ruleset?.appliesToType === undefined });
                                } }) }), _jsx(Grid, { size: { xs: 12, md: 'auto' }, children: _jsx(FormControlLabel, { label: `Apply to all ${ruleset?.appliesToType !== undefined ? pluralReadableTypeName(ruleset.appliesToType) : ''}`, labelPlacement: "start", sx: { paddingInlineEnd: 1 }, disabled: !editable || saving || updating || deleting || ruleset?.appliesToType === undefined || linkedGroups.length !== 0, control: _jsx(Switch, { checked: ruleset?.appliesToType !== undefined && ruleset.globallyApplyToObjectTypeId, onChange: (_, checked) => {
                                        setRuleset((r) => {
                                            const newRuleset = new Ruleset(r ?? {});
                                            newRuleset.globallyApplyToObjectTypeId = checked;
                                            return newRuleset;
                                        });
                                    } }) }) })] }), _jsx(Grid, { size: 12, children: _jsx(Autocomplete, { value: linkedGroups.map((group) => group.sk), options: groups.sort((a, b) => a.name?.localeCompare(b.name ?? '') ?? 0).map((group) => group.sk), getOptionLabel: (option) => groups.filter((group) => group.sk === option).shift()?.name ?? option, multiple: true, freeSolo: false, size: "small", fullWidth: true, disabled: !editable || saving || updating || deleting || ruleset?.appliesToType === undefined || ruleset.globallyApplyToObjectTypeId, onChange: (_, value) => {
                            setLinkedGroups((groups) => updateGroupLinks(groups.map((group) => group.sk), value).map((sk) => groups.filter((group) => group.sk === sk).shift() ?? { pk: sk, sk: sk, type: GroupClassName, id: '' }));
                        }, renderInput: (props) => (_jsx(TextField, { ...props, label: `Groups (${groups.length})`, error: !deleting && ruleset?.appliesToType !== undefined && !ruleset.globallyApplyToObjectTypeId && linkedGroups?.length === 0 })) }) }), _jsx(Grid, { size: { xs: 12, md: 'auto' }, children: _jsx(Autocomplete, { value: setOperation, disableClearable: true, options: ['AND', 'OR'], size: "small", disabled: !editable || saving || updating || deleting, onChange: (_, value) => {
                            if (['AND', 'OR', undefined].includes(value)) {
                                setSetOperation(value);
                            }
                        }, getOptionLabel: (option) => (option === 'AND' ? 'All' : 'Any'), renderInput: (params) => _jsx(AutosizedTextField, { ...params, label: "Trigger", error: !deleting && setOperation === undefined }) }) }), setOperation && (_jsx(Grid, { size: { xs: 12, md: 'grow' }, children: _jsx(Alert, { severity: "info", sx: { paddingBlock: 0, height: '100%', alignItems: 'center' }, children: setOperation === 'AND' ? 'All rules must be violated to trigger an incident' : 'Any one rule violation will trigger an incident' }) }))] }, "trigger")),
        valid: HoursPeriodSelector.HoursPeriodSelector,
        message: (_jsxs(IoTVAccordion, { sx: { width: '100%' }, children: [_jsx(IoTVAccordionSummary, { children: _jsx(FormLabel, { children: "Message template" }) }), _jsx(IoTVAccordionDetails, { children: _jsx(MessageTemplate, { variant: "normal", disabled: !editable || saving || updating || deleting, rulesetTemplate: ruleset?.messageTemplate, setRulesetTemplate: (messageTemplate) => setRuleset((r) => {
                            const newRuleset = new Ruleset(r ?? {});
                            newRuleset.messageTemplate = messageTemplate;
                            return newRuleset;
                        }) }) })] }, "message_template")),
        escalation: (_jsxs(IoTVAccordion, { sx: { width: '100%' }, children: [_jsx(IoTVAccordionSummary, { children: _jsxs(Stack, { direction: "row", sx: { alignItems: 'center', gap: 1, marginBlock: -1 }, children: [_jsx(FormLabel, { children: "Escalation template" }), _jsx(Alert, { severity: "info", sx: { paddingBlock: 0, alignItems: 'center' }, children: "This will be appended to the normal notification message" })] }) }), _jsx(IoTVAccordionDetails, { children: _jsx(MessageTemplate, { variant: "escalation", disabled: !editable || saving || updating || deleting, rulesetTemplate: ruleset?.escalationTemplate, setRulesetTemplate: (escalationTemplate) => {
                            setRuleset((r) => {
                                const newRuleset = new Ruleset(r ?? {});
                                newRuleset.escalationTemplate = escalationTemplate;
                                return newRuleset;
                            });
                        } }) })] }, "escalation_template")),
        period: (_jsxs(Stack, { direction: isSmallView ? 'column' : 'row', alignItems: "stretch", gap: 1, children: [_jsx(AutosizedTextField, { value: ruleset?.period, label: "Period", size: "small", fullWidth: isSmallView, error: !deleting && hasTimeDependentRules ? ruleset.period === undefined : ruleset?.period !== undefined, disabled: !editable || saving || updating || deleting || noRuleset || (noPeriod && !hasTimeDependentRules), onChange: (event) => {
                        setRuleset((r) => {
                            const newRuleset = new Ruleset(r ?? {});
                            newRuleset.period = event.target.value ? Number.parseInt(event.target.value) : undefined;
                            if (Number.isNaN(newRuleset.period))
                                newRuleset.period = undefined;
                            return newRuleset;
                        });
                    } }), _jsx(Autocomplete, { value: ruleset?.unit ?? null, options: Object.keys(TimeBucketUnits)
                        .filter((v) => !isNaN(Number(v)))
                        .map(Number), getOptionLabel: (option) => Object.entries(TimeBucketUnits)
                        .filter(([_, value]) => value === option)
                        .map(([key]) => toTitleCase(key))
                        .map(pluralize)
                        .shift() ?? option.toString(), freeSolo: false, size: "small", fullWidth: isSmallView, disabled: !editable || saving || updating || deleting || noRuleset || (noUnit && !hasTimeDependentRules), onChange: (_, value) => {
                        setRuleset((r) => {
                            const newRuleset = new Ruleset(r ?? {});
                            newRuleset.unit = (value ?? undefined);
                            return newRuleset;
                        });
                    }, slots: { popper: AutosizedPopper }, renderInput: (props) => (_jsx(AutosizedTextField, { ...props, label: "Unit", error: !deleting && hasRuleset && hasTimeDependentRules ? ruleset.unit === undefined : ruleset?.unit !== undefined })) }), _jsx(Alert, { severity: "info", sx: { paddingBlock: 0, alignItems: 'center' }, children: "Configure how much history to use when evaluating bucketed metric rules" })] }, "metric_config")),
        boolean: (_jsxs(IoTVAccordion, { sx: { width: '100%' }, children: [_jsx(IoTVAccordionSummary, { children: _jsx(FormLabel, { children: "Boolean rules" }) }), _jsx(IoTVAccordionDetails, { children: _jsxs(Stack, { gap: 1, children: [(ruleset?.definition.BooleanRule === undefined || ruleset.definition.BooleanRule?.length === 0) && (_jsx(Typography, { sx: { fontStyle: 'italic', opacity: 0.75, marginInlineStart: 1 }, children: "No Boolean rules" })), ruleset?.definition.BooleanRule?.map((booleanRule, i) => {
                                const setRuleInParent = (rule) => {
                                    const newRuleset = new Ruleset(ruleset);
                                    const booleanRules = newRuleset.definition.BooleanRule;
                                    booleanRules?.splice(i, 1, rule);
                                    setRuleset(newRuleset);
                                };
                                const deleteRuleInParent = () => {
                                    const newRuleset = new Ruleset(ruleset);
                                    const booleanRules = newRuleset.definition.BooleanRule;
                                    booleanRules?.splice(i, 1);
                                    setRuleset(newRuleset);
                                };
                                return (_jsx(RulesetBooleanRuleCard, { ...props,
                                    user,
                                    userSelectedGroup,
                                    userFunctions,
                                    userGroupRoles,
                                    booleanRule,
                                    metricKeysEnum,
                                    ruleAttributes,
                                    userAttributes,
                                    removeArrayObjectNInParent: deleteRuleInParent,
                                    setBooleanRule: setRuleInParent,
                                    disabled: !editable || saving || updating || deleting }, `${i}_${booleanRule.key}`));
                            })] }) }), _jsx(AccordionActions, { children: _jsx(Button, { startIcon: _jsx(AddRounded, {}), variant: "contained", color: "primary", onClick: addBooleanRule, disabled: !editable || saving || updating || deleting || ruleset?.appliesToType === undefined, children: "Add Boolean Rule" }) })] }, 'boolean_rules')),
        metric: (_jsxs(IoTVAccordion, { sx: { width: '100%' }, children: [_jsx(IoTVAccordionSummary, { children: _jsx(FormLabel, { children: "Metric rules" }) }), _jsx(IoTVAccordionDetails, { children: _jsxs(Stack, { gap: 1, children: [(ruleset?.definition.MetricRule === undefined || ruleset.definition.MetricRule?.length === 0) && (_jsx(Typography, { sx: { fontStyle: 'italic', opacity: 0.75, marginInlineStart: 1 }, children: "No metric rules" })), ruleset?.definition.MetricRule?.map((metricRule, i) => {
                                const setRuleInParent = (rule) => {
                                    const newRuleset = new Ruleset(ruleset);
                                    const metricRules = newRuleset.definition.MetricRule;
                                    metricRules?.splice(i, 1, rule);
                                    setRuleset(newRuleset);
                                };
                                const deleteRuleInParent = () => {
                                    const newRuleset = new Ruleset(ruleset);
                                    const metricRules = newRuleset.definition.MetricRule;
                                    metricRules?.splice(i, 1);
                                    setRuleset(newRuleset);
                                };
                                return (_jsx(RulesetMetricRuleCard, { ...props,
                                    user,
                                    userSelectedGroup,
                                    userFunctions,
                                    userGroupRoles,
                                    metricRule,
                                    metricKeysEnum,
                                    ruleAttributes,
                                    removeArrayObjectNInParent: deleteRuleInParent,
                                    setMetricRule: setRuleInParent,
                                    disabled: !editable || saving || updating || deleting }, `${i}_${metricRule.key}`));
                            })] }) }), _jsx(AccordionActions, { children: _jsx(Button, { startIcon: _jsx(AddRounded, {}), variant: "contained", color: "primary", onClick: addMetricRule, disabled: !editable || saving || updating || deleting || ruleset?.appliesToType === undefined, children: "Add Metric Rule" }) })] }, 'metric_rules')),
        comparison: (_jsxs(IoTVAccordion, { sx: { width: '100%' }, children: [_jsx(IoTVAccordionSummary, { children: _jsx(FormLabel, { children: "Comparison rules" }) }), _jsx(IoTVAccordionDetails, { children: _jsxs(Stack, { gap: 1, children: [(ruleset?.definition.ComparisonRule === undefined || ruleset.definition.ComparisonRule?.length === 0) && (_jsx(Typography, { sx: { fontStyle: 'italic', opacity: 0.75, marginInlineStart: 1 }, children: "No Comparison rules" })), ruleset?.definition.ComparisonRule?.map((comparisonRule, i) => {
                                const setRuleInParent = (rule) => {
                                    const newRuleset = new Ruleset(ruleset);
                                    const comparisonRules = newRuleset.definition.ComparisonRule;
                                    comparisonRules?.splice(i, 1, rule);
                                    setRuleset(newRuleset);
                                };
                                const deleteRuleInParent = () => {
                                    const newRuleset = new Ruleset(ruleset);
                                    const comparisonRules = newRuleset.definition.ComparisonRule;
                                    comparisonRules?.splice(i, 1);
                                    setRuleset(newRuleset);
                                };
                                return (_jsx(RulesetComparisonRuleCard, { ...props,
                                    user,
                                    userSelectedGroup,
                                    userFunctions,
                                    userGroupRoles,
                                    comparisonRule,
                                    metricKeysEnum,
                                    ruleAttributes,
                                    userAttributes,
                                    removeArrayObjectNInParent: deleteRuleInParent,
                                    setComparisonRule: setRuleInParent,
                                    disabled: !editable || saving || updating || deleting }, `${i}_${comparisonRule.key}`));
                            })] }) }), _jsx(AccordionActions, { children: _jsx(Button, { startIcon: _jsx(AddRounded, {}), variant: "contained", color: "primary", onClick: addComparisonRule, disabled: !editable || saving || updating || deleting || ruleset?.appliesToType === undefined, children: "Add Comparison Rule" }) })] }, 'comparison_rules')),
    };
    return (_jsx(Card, { children: _jsx(CardContent, { children: _jsxs(Stack, { gap: 1, children: [components.name, _jsxs(Grid, { container: true, spacing: 1, children: [components.description, components.trigger] }), components.valid, _jsxs(Stack, { children: [components.message, components.escalation] }, 'templates'), components.period, _jsxs(Stack, { children: [components.boolean, components.metric, components.comparison] }, 'rules')] }) }) }));
};
